/**
 * TypeScript Types for Credit System
 *
 * Credit-based billing system types.
 */

// ===========================================
// CREDIT BALANCE
// ===========================================

/**
 * Workspace credit balance
 */
export interface CreditBalance {
  available_credits: number
  reserved_credits: number
  effective_available: number
  subscription_credits: number
  purchased_credits: number
  rollover_credits: number
  monthly_allocated: number
  monthly_used: number
  billing_period_start: string | null
  billing_period_end: string | null
  package_id: string | null
  storage: StorageInfo
  lifetime: LifetimeStats
}

export interface StorageInfo {
  used_bytes: number
  limit_bytes: number
  used_gb: number
  limit_gb: number
  percentage: number
}

export interface LifetimeStats {
  purchased: number
  used: number
  earned: number
}

// ===========================================
// CREDIT TOP-UP PACKAGES (One-Time Purchase)
// ===========================================

/**
 * One-time credit top-up package
 */
export interface CreditTopUpPackage {
  id: string
  credits: number
  price_eur: number
  savings_percent: number | null
  is_popular: boolean
}

export interface CreditTopUpPackagesResponse {
  packages: CreditTopUpPackage[]
}

// ===========================================
// CREDIT PACKAGES
// ===========================================

/**
 * Credit package (subscription tier)
 */
export interface CreditPackage {
  id: string
  code: string
  name: string
  display_name: Record<string, string>
  credits_monthly: number
  credits_rollover: boolean
  monthly_price_eur: number
  annual_price_eur: number | null
  price_per_extra_credit_eur: number | null
  storage_included_gb: number
  storage_price_per_gb_eur: number
  color: string | null
  marketing_features: string[]
}

// ===========================================
// CREDIT TRANSACTIONS
// ===========================================

/**
 * Credit transaction record
 */
export interface CreditTransaction {
  id: string
  transaction_type: TransactionType
  operation_type: string | null
  credit_amount: number
  balance_before: number
  balance_after: number
  source_type: string | null
  description: string | null
  created_at: string
}

export type TransactionType =
  | 'credit'
  | 'debit'
  | 'reserve'
  | 'release'
  | 'expire'
  | 'refund'
  | 'adjustment'

// ===========================================
// CREDIT PRICING
// ===========================================

/**
 * Credit cost for an operation
 */
export interface CreditPricing {
  feature_code: string
  feature_name: string
  display_name: Record<string, string>
  base_credits: number
  effective_credits: number
  category: string | null
}

// ===========================================
// CREDIT CHECK
// ===========================================

/**
 * Result of credit availability check
 */
export interface CreditCheckResult {
  can_proceed: boolean
  credits_required: number
  credits_available: number
  error_message: string | null
}

// ===========================================
// COST PREVIEW
// ===========================================

/**
 * Cost preview for multiple operations
 */
export interface CostPreviewRequest {
  operations: CostPreviewOperation[]
}

export interface CostPreviewOperation {
  operation_type: string
  count: number
}

export interface CostPreviewResult {
  breakdown: CostPreviewItem[]
  total_credits: number
  credits_available: number
  credits_remaining: number
  can_proceed: boolean
}

export interface CostPreviewItem {
  operation_type: string
  count: number
  credits_per_unit: number
  credits_total: number
  error?: string
}

// ===========================================
// API RESPONSES
// ===========================================

export interface CreditBalanceResponse {
  balance: CreditBalance
}

export interface CreditPackagesResponse {
  packages: CreditPackage[]
}

export interface CreditPricingResponse {
  pricing: CreditPricing[]
}

export interface CreditTransactionsResponse {
  transactions: CreditTransaction[]
  limit: number
  offset: number
}

export interface StorageResponse {
  storage: StorageInfo
}

// ===========================================
// HELPERS
// ===========================================

/**
 * Get transaction type label
 */
export function getTransactionTypeLabel(type: TransactionType): string {
  const labels: Record<TransactionType, string> = {
    credit: 'Gutschrift',
    debit: 'Abbuchung',
    reserve: 'Reserviert',
    release: 'Freigegeben',
    expire: 'Abgelaufen',
    refund: 'Rückerstattung',
    adjustment: 'Anpassung',
  }
  return labels[type] || type
}

/**
 * Get transaction type color
 */
export function getTransactionTypeColor(type: TransactionType): string {
  switch (type) {
    case 'credit':
    case 'refund':
    case 'release':
      return 'text-green-600 dark:text-green-400'
    case 'debit':
    case 'reserve':
      return 'text-red-600 dark:text-red-400'
    case 'expire':
      return 'text-gray-500 dark:text-gray-400'
    case 'adjustment':
      return 'text-primary-600 dark:text-primary-400'
    default:
      return 'text-gray-600 dark:text-gray-300'
  }
}

/**
 * Check if credits are low (< 20% of monthly allocation)
 */
export function isCreditsLow(balance: CreditBalance): boolean {
  if (balance.monthly_allocated === 0) return false
  const percentage = (balance.available_credits / balance.monthly_allocated) * 100
  return percentage < 20
}

/**
 * Check if storage is near limit (> 80%)
 */
export function isStorageNearLimit(storage: StorageInfo): boolean {
  return storage.percentage > 80
}
